import { LoadState } from "@redotech/react-util/load";
import { CustomerTagProvider } from "@redotech/redo-model/customer-tags/customer-tag-record";
import { PillTheme } from "@redotech/redo-model/pill-theme";
import { TagGroup } from "@redotech/redo-model/support/tag-group-zod-schema";
import { RedoModal } from "@redotech/redo-web/arbiter-components/modal/redo-modal";
import { memo, useState } from "react";
import { TagInputFields } from "./tag-fields";

// Tag like object, less restrictive on "source" so we can reuse this component
export type TagWithId = {
  name: string;
  pillTheme: PillTheme;
  tagId: string;
  source: string;
};

export const CreateTagModal = memo(function CreateTagModal({
  cancelClicked,
  tagsLoad,
  handleCreateTag,
  tagGroups = [],
  showProviderSelector = false,
}: {
  cancelClicked(): void;
  tagsLoad: LoadState<TagWithId[]>;
  handleCreateTag: ({
    name,
    pillTheme,
    tagGroupId,
    provider,
  }: {
    name: string;
    pillTheme: PillTheme;
    tagGroupId?: string | null;
    provider: CustomerTagProvider;
  }) => Promise<void>;
  tagGroups?: TagGroup[];
  showProviderSelector?: boolean;
}) {
  const existingTagNames = new Set(
    tagsLoad.value?.map((tag) => tag.name.toLowerCase()),
  );

  const [pillTheme, setPillTheme] = useState<PillTheme>(
    PillTheme.PRIMARY_LIGHT,
  );
  const [name, setName] = useState("");
  const [provider, setProvider] = useState<CustomerTagProvider>(
    CustomerTagProvider.REDO,
  );
  const [selectedTagGroup, setSelectedTagGroup] = useState<string | null>(null);

  async function handleSubmit() {
    await handleCreateTag({
      name,
      pillTheme,
      tagGroupId: selectedTagGroup,
      provider,
    });
  }

  const nameConflict = existingTagNames.has(name.toLowerCase());

  return (
    <RedoModal
      isOpen
      onModalCloseRequested={cancelClicked}
      primaryButton={{
        text: "Create",
        onClick: handleSubmit,
        disabled: !name || nameConflict,
      }}
      secondaryButton={{ text: "Cancel", onClick: cancelClicked }}
      title="Create tag"
    >
      <TagInputFields
        name={name}
        pillTheme={pillTheme}
        provider={provider}
        selectedTagGroup={selectedTagGroup}
        setName={setName}
        setPillTheme={setPillTheme}
        setProvider={showProviderSelector ? setProvider : undefined}
        setSelectedTagGroup={setSelectedTagGroup}
        showNameError={nameConflict}
        tagGroups={tagGroups}
      />
    </RedoModal>
  );
});
